/*
 * Copyright (c) 2026 Joseph Duggan. All rights reserved.
 */

import '@aws-amplify/ui-react/styles.css';
import { Authenticator } from '@aws-amplify/ui-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { signOut } from 'aws-amplify/auth';
import { useState } from 'react';
import { AppShell, type NavMode } from './components/AppShell.js';
import './lib/amplify.js'; // Amplify.configure side-effect
import { makeTrpcClient, trpc } from './lib/trpc.js';
import { AccountPage } from './pages/Account.js';
import { ConstitutionPage } from './pages/Constitution.js';
import { CounselPage } from './pages/Counsel.js';
import { DocketPage } from './pages/Docket.js';
import { GoogleOauthCallbackPage } from './pages/GoogleOauthCallback.js';
import { LetterPage } from './pages/Letter.js';
import { QuarterlyReviewPage } from './pages/QuarterlyReview.js';
import { RecordPage } from './pages/Record.js';
import { WeeklyReviewPage } from './pages/WeeklyReview.js';

function AuthenticatedApp({ username }: { username: string | undefined }) {
  const [queryClient] = useState(() => new QueryClient());
  const [trpcClient] = useState(() => makeTrpcClient());
  const [mode, setMode] = useState<NavMode>('letter');
  // Google's OAuth redirect lands on a real URL path (CloudFront's SPA
  // fallback serves index.html for it). It is not a NavMode — detect it
  // at boot and render the callback page instead of the shell; when the
  // exchange settles we replaceState back to '/' and land on The Account.
  const [oauthCallback, setOauthCallback] = useState(
    () => window.location.pathname === '/oauth/google/callback',
  );
  const [calendarJustConnected, setCalendarJustConnected] = useState(false);

  if (oauthCallback) {
    return (
      <trpc.Provider client={trpcClient} queryClient={queryClient}>
        <QueryClientProvider client={queryClient}>
          <GoogleOauthCallbackPage
            onDone={(connected) => {
              window.history.replaceState(null, '', '/');
              setCalendarJustConnected(connected);
              setMode('account');
              setOauthCallback(false);
            }}
          />
        </QueryClientProvider>
      </trpc.Provider>
    );
  }

  return (
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>
        <AppShell
          currentMode={mode}
          onModeChange={setMode}
          onSignOut={() => {
            void signOut();
          }}
          signedInAs={username}
        >
          {mode === 'account' ? (
            <AccountPage calendarJustConnected={calendarJustConnected} />
          ) : mode === 'counsel' ? (
            <CounselPage />
          ) : mode === 'docket' ? (
            <DocketPage />
          ) : mode === 'constitution' ? (
            <ConstitutionPage />
          ) : mode === 'weekly' ? (
            <WeeklyReviewPage />
          ) : mode === 'quarterly' ? (
            <QuarterlyReviewPage />
          ) : mode === 'record' ? (
            <RecordPage />
          ) : (
            <LetterPage />
          )}
        </AppShell>
      </QueryClientProvider>
    </trpc.Provider>
  );
}

export function App() {
  return (
    <Authenticator
      // Email-only sign-in matches the Cognito user pool config in
      // packages/infra/lib/auth-stack.ts (email is the alias attribute).
      loginMechanisms={['email']}
      signUpAttributes={['email']}
    >
      {({ user }) => <AuthenticatedApp username={user?.signInDetails?.loginId} />}
    </Authenticator>
  );
}
